import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { endImpersonation, impersonateUser } from "@/api/openapi";
import { DESKTOP_WIDTH, TABLET_WIDTH } from "./constants";
import { ensurePushUnsubscribed } from "./push";
import { PspContext } from "./contexts";
import { translate } from "./translator";
import {
  getAccessToken,
  getAdminSessionStash,
  getRefreshToken,
  getSelectedMandateId,
  setAccessTokenStorage,
  setActorIdStorage,
  setAdminSessionStash,
  setRefreshTokenStorage,
} from "./utils";

export function useIsTablet() {
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const tablet = TABLET_WIDTH;

    const match = window.matchMedia(`(min-width: ${tablet})`);

    const update = () => setIsTablet(match.matches);
    update();

    match.addEventListener("change", update);
    return () => match.removeEventListener("change", update);
  }, []);

  return isTablet;
}

export function useImpersonation() {
  const {
    lang,
    user,
    setUser,
    setLang,
    actorId,
    setActorId,
    setSelectedMandate,
    setSelectedMandateId,
  } = useContext(PspContext);
  const navigate = useNavigate();

  function startImpersonating(userId: string) {
    impersonateUser({ path: { user_id: userId } }).then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
        return;
      }

      const adminAccessToken = getAccessToken();
      const adminRefreshToken = getRefreshToken();
      if (user && adminAccessToken && adminRefreshToken) {
        setAdminSessionStash({
          accessToken: adminAccessToken,
          refreshToken: adminRefreshToken,
          user,
        });
      }

      setAccessTokenStorage(data.accessToken);
      setRefreshTokenStorage(data.refreshToken);
      setActorIdStorage(data.actorId);

      setUser(data.user);
      setLang(data.user.language.short);
      setActorId(data.actorId ?? null);

      const firstMandateRight = data.user.mandateRights[0];
      setSelectedMandate(firstMandateRight?.mandate);
      setSelectedMandateId(firstMandateRight?.mandate.id ?? null);

      navigate("/home");
    });
  }

  async function returnToAdmin() {
    try {
      // best effort — a network hiccup or near-expired token shouldn't
      // strand the admin in someone else's account
      await endImpersonation();
    } catch {
      // ignored
    }

    const stash = getAdminSessionStash();
    if (!stash) {
      // can't safely reconstruct the admin identity from an
      // impersonation token alone
      setAccessTokenStorage(null);
      setRefreshTokenStorage(null);
      setActorIdStorage(null);
      navigate("/login");
      return;
    }

    setAccessTokenStorage(stash.accessToken);
    setRefreshTokenStorage(stash.refreshToken);
    setActorIdStorage(null);
    setAdminSessionStash(null);

    setUser(stash.user);
    setLang(stash.user.language.short);
    setActorId(null);

    const restoredMandateId = getSelectedMandateId();
    const restoredMandateRight =
      stash.user.mandateRights.find(
        (mandateRight) => mandateRight.mandate.id === restoredMandateId,
      ) ?? stash.user.mandateRights[0];
    setSelectedMandate(restoredMandateRight?.mandate);
    setSelectedMandateId(restoredMandateRight?.mandate.id ?? null);

    navigate("/admin/users");
  }

  return {
    isImpersonating: actorId !== null,
    startImpersonating,
    returnToAdmin,
  };
}

export function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const desktop = DESKTOP_WIDTH;

    const match = window.matchMedia(`(min-width: ${desktop})`);

    const update = () => setIsDesktop(match.matches);
    update();

    match.addEventListener("change", update);
    return () => match.removeEventListener("change", update);
  }, []);

  return isDesktop;
}

type SWMessage =
  | { type: "PUSH_SUBSCRIPTION_CHANGED"; payload: PushSubscription | null }
  | { type: "PUSH_PERMISSION_REVOKED" }
  | { type: string; payload?: unknown };

export function usePushSubscriptionSync(accessToken: string | null) {
  useEffect(() => {
    if (!accessToken) return; // only listen when the user is logged in
    if (!("serviceWorker" in navigator)) return;

    const onMessage = async (event: MessageEvent<SWMessage>) => {
      const msg = event.data;
      if (!msg) return;

      if (msg.type === "PUSH_SUBSCRIPTION_CHANGED") {
        const sub = msg.payload;
        if (!sub) {
          // Likely permission revoked → tell backend to stop sending
          await ensurePushUnsubscribed(accessToken);
          return;
        }
        // New/rotated subscription → upsert using the page’s auth
        await fetch(`${import.meta.env.VITE_BASE_URL}/api/push/subscribe`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(sub),
        });
      }

      if (msg.type === "PUSH_PERMISSION_REVOKED") {
        await ensurePushUnsubscribed(accessToken);
      }
    };

    navigator.serviceWorker.addEventListener("message", onMessage);

    // Optional: reconcile on mount (covers messages that arrive before listener or missed events)
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        // If permission is denied, make sure backend is cleaned up
        if (Notification.permission === "denied" && sub) {
          await ensurePushUnsubscribed(accessToken);
        }
        // If granted and there is a sub, make sure backend knows about it
        if (Notification.permission === "granted" && sub) {
          await fetch(`${import.meta.env.VITE_BASE_URL}/api/push/subscribe`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(sub),
          });
        }
      } catch {}
    })();

    return () => {
      navigator.serviceWorker.removeEventListener("message", onMessage);
    };
  }, [accessToken]);
}
